import debounce from 'awesome-debounce-promise';
import { emptyToUndefined, GET, isGranted, isValidEmail, MPOST, MPUT, POST } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import type { ROLE, Role } from 'types/Role.ts';
import type { User } from 'types/User.ts';
import { POC_POD_TEMPLATES } from 'types/User.ts';
import { Button } from 'ui/component/Button.tsx';
import { Divider } from 'ui/component/Divider.tsx';
import { ICheck } from 'ui/component/Icons.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Input } from 'ui/control/Input.tsx';
import { SelectMulti } from 'ui/control/SelectMulti.tsx';
import { getTranslatedError } from '#admin/helpers.ts';
import { useEnv } from '#admin/hook/useEnv.tsx';
import { useMe } from '#admin/hook/useMe.tsx';

type FormValues = {
  id?: string;
  logistics_ex_id: string;
  name: string;
  email: string;
  phone: string;
  company_name: string;
  company_website: string;
  vat_number: string;
  address: string;
  role: ROLE | '';
  owner_id: string;
  send_credentials: boolean;
  trackingmore_id: string;
  poc_pod_template: string;
};

export type GroupFormProps = {
  groupId?: string;
  onSave?: (group: User) => void;
  onClose?: () => void;
  isOpen: boolean;
};

export const GroupForm = ({ groupId, onSave, onClose, isOpen }: GroupFormProps) => {
  const { t } = useTranslation();
  const { me } = useMe();
  const env = useEnv();
  const isEdit = !!groupId;
  const thisGroupSWR = useSWR<User>(isEdit ? [`/user/${groupId}`] : null, GET);
  const groupsSWR = useSWR<User[]>(['/user/list', { is_group: true, sort: [['name', 'asc']] }], POST);
  const rolesSWR = useSWR<Role[]>(['/role/list', { sort: [['name', 'asc']] }], POST);
  const trackingmoreSWR = useSWR(['/enum/trackingmore/carriers'], GET);
  const allGroups = groupsSWR.data;
  const thisGroup = thisGroupSWR.data;
  const allRoles = rolesSWR.data;

  const createMutation = useSWRMutation('/user', MPOST);
  const updateMutation = useSWRMutation(`/user/${groupId}`, MPUT);
  const existsMutation = useSWRMutation('/user/exists', MPOST);

  const isLoading =
    createMutation.isMutating ||
    updateMutation.isMutating ||
    groupsSWR.isValidating ||
    (groupId && thisGroupSWR.isValidating) ||
    rolesSWR.isValidating ||
    trackingmoreSWR.isValidating;
  const isError = createMutation.error || updateMutation.error || groupsSWR.error || (groupId && thisGroupSWR.error) || rolesSWR.error;

  const availableParents = React.useMemo(() => {
    if (!allGroups) return {};
    const options: Record<string, string> = {};
    for (const group of allGroups.filter((group) => group.id !== groupId)) {
      options[group.id] = `${group.name} (role=${group.role})`;
    }
    return options;
  }, [allGroups, groupId]);

  const availableRoles = React.useMemo(() => {
    if (!allRoles) return [];
    const roles = allRoles.map((role) => role.name).filter((name) => name !== 'MEMBER');
    return roles;
  }, [allRoles]);

  const availableTrackingmoreCodes: Record<string, string> = React.useMemo(() => {
    if (!trackingmoreSWR.data) return {};
    const result: Record<string, string> = {};
    for (const supplier of trackingmoreSWR.data) result[supplier.courier_code] = supplier.courier_name || supplier.courier_code;
    const sorted_result = Object.fromEntries(Object.entries(result).sort(([, a], [, b]) => a.localeCompare(b)));
    return sorted_result;
  }, [trackingmoreSWR.data]);

  const formMethods = useForm<FormValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });

  const { register, handleSubmit, watch, reset, formState } = formMethods;

  React.useEffect(() => {
    if (isEdit && thisGroup) reset(thisGroup as any); // tofix: schema correctly has string|null for fields, but in form we want '' instead of null
  }, [reset, isEdit, thisGroup]);

  const onSubmit = async (values: FormValues) => {
    const selectedParent = allGroups?.find((company) => company.id === values.owner_id);
    if (!selectedParent) return;

    const payload: Record<string, any> = {
      ...emptyToUndefined(values),
      email: isEdit && thisGroup?.email === values.email ? undefined : values.email,
    };

    // only add for creation as update does not accept this field
    if (!isEdit) payload.is_group = true;

    let response: User;
    if (isEdit) response = await updateMutation.trigger(payload);
    else response = await createMutation.trigger(payload);

    if (onSave) void onSave(response);
  };

  const isUniqueLogisticsExId = React.useMemo(
    () =>
      // @ts-expect-error
      debounce(async (value: string) => {
        if (!value || (isEdit && value === thisGroup?.logistics_ex_id)) return true;
        const groupExists = await existsMutation.trigger({ logistics_ex_id: value });
        return !groupExists;
      }, 200),
    [existsMutation, isEdit, thisGroup],
  );

  const isUniqueEmail = React.useMemo(
    () =>
      // @ts-expect-error
      debounce(async (value: string) => {
        // This is to avoid sending unnecessary requests when the user is typing the email.
        if (!isValidEmail(value)) return false;

        // This form is used both for user creation and updates (eg: profile).
        // If the user is changing its data, the user is already saved and the email is already in db.
        // In that case, we check that the email is not in use by another user ONLY IF the user is changing it.
        if (isEdit && value === thisGroup?.email) return true;
        const groupExists = await existsMutation.trigger({ email: value });
        return !groupExists;
      }, 200),
    [isEdit, thisGroup?.email, existsMutation],
  );

  const role = watch('role');

  return (
    <Modal title={<h3>{isEdit ? t('components.groupForm.title.edit', { name: thisGroup?.name }) : t('components.groupForm.title.new')}</h3>} isOpen={isOpen} onClose={onClose}>
      {(!me || isLoading) && <Spinner centered={true} overlay={true} blur={true} />}

      <FormProvider {...formMethods}>
        <form className="w-[500px]" autoCorrect={'off'} autoComplete={'off'} onSubmit={handleSubmit(onSubmit)} noValidate={true}>
          {groupId && <input type={'hidden'} {...register('id')} />}
          <div className="flex flex-col gap-6 overflow-y-scroll px-0.5">
            <Input
              className="w-full"
              name={'name'}
              label={t('components.groupForm.labels.name')}
              placeholder={t('components.groupForm.placeholders.name')}
              registerOptions={{ required: true }}
              autoFocus={false}
            >
              {formState.errors.name?.type === 'required' ? t('components.groupForm.errors.required') : null}
            </Input>

            <Input
              name={'email'}
              label={t('components.groupForm.labels.email')}
              placeholder={t('components.groupForm.placeholders.email')}
              disabled={isEdit && thisGroup?.sso_only}
              registerOptions={{ required: true, validate: { isEmail: isValidEmail, isUnique: isUniqueEmail } }}
            >
              {formState.errors.email?.type === 'required' ? t('components.groupForm.errors.required') : null}
              {formState.errors.email?.type === 'isUnique' ? t('components.groupForm.errors.emailInUse') : null}
              {formState.errors.email?.type === 'isEmail' ? t('components.groupForm.errors.invalidEmail') : null}
            </Input>

            <SelectMulti
              className="w-full"
              name={'owner_id'}
              label={t('components.groupForm.labels.parentGroup')}
              options={availableParents}
              selectable={'one'}
              closeOnChangedValue={true}
              required={true}
              hasClearAll={true}
            />

            <SelectMulti
              className="w-full"
              name={'role'}
              label={t('components.groupForm.labels.role')}
              options={availableRoles}
              selectable={'one'}
              closeOnChangedValue={true}
              required={true}
              hasClearAll={true}
            />

            <Input
              className="w-full"
              name={'logistics_ex_id'}
              label={t('components.groupForm.labels.logisticsId')}
              placeholder={t('components.groupForm.placeholders.logisticsId')}
              registerOptions={{ validate: { isUnique: isUniqueLogisticsExId } }}
              autoFocus={false}
            >
              {formState.errors.logistics_ex_id?.type === 'isValidLogisticsExId' ? t('components.groupForm.errors.invalidLogisticsId') : null}
              {formState.errors.logistics_ex_id?.type === 'isUnique' ? t('components.groupForm.errors.logisticsIdInUse') : null}
            </Input>

            {role === 'SUPPLIER' && isGranted(me, 'UserSetTrackingMoreID', `${env.SUPERADMIN_GROUP_ID}/*`) && (
              <SelectMulti
                name={'trackingmore_id'}
                label={t('components.groupForm.labels.trackingmore_supplier_id')}
                options={availableTrackingmoreCodes}
                selectable={'one'}
                closeOnChangedValue={true}
              />
            )}

            <SelectMulti
              className="w-full"
              name={'poc_pod_template'}
              label={t('components.groupForm.labels.pocPodTemplate')}
              options={POC_POD_TEMPLATES.reduce<Record<string, string>>((acc, v) => ({ ...acc, [v]: t(`components.groupForm.podTemplateOptions.${v}`) }), {})}
              selectable={'one'}
              closeOnChangedValue={true}
              hasClearAll={true}
            />

            <Divider>{t('components.groupForm.dividers.companyInfo')}</Divider>

            <Input className="w-full" name={'company_name'} label={t('components.groupForm.labels.companyName')} placeholder={t('components.groupForm.placeholders.companyName')} />
            <div className="flex flex-row gap-4">
              <Input className="w-full" name={'address'} label={t('components.groupForm.labels.address')} placeholder={t('components.groupForm.placeholders.address')} />
              <Input className="w-full" name={'company_website'} label={t('components.groupForm.labels.website')} placeholder={t('components.groupForm.placeholders.website')} />
            </div>
            <div className="flex flex-row gap-4">
              <Input className="w-full" name={'phone'} label={t('components.groupForm.labels.phoneNumber')} placeholder={t('components.groupForm.placeholders.phoneNumber')} />
              <Input className="w-full" name={'vat_number'} label={t('components.groupForm.labels.vatNumber')} placeholder={t('components.groupForm.placeholders.vatNumber')} />
            </div>

            {/*{!isEdit && <Checkbox name={'send_credentials'} label={t('components.groupForm.labels.sendCredentials')} />}*/}
          </div>

          <div className="mt-8 flex items-center gap-4">
            <Button type="button" className="blue-outlined col-span-2 justify-self-end" onClick={onClose}>
              {t('components.groupForm.buttons.back')}
            </Button>
            <Button
              className="blue col-span-2 justify-self-end"
              loading={isLoading}
              disabled={!formState.isValid || !formState.isDirty}
              RightIcon={!isLoading && formState.isSubmitSuccessful ? ICheck : undefined}
              type="submit"
            >
              {t('components.groupForm.buttons.save')}
            </Button>
          </div>

          {!!isError && <div className="mt-2 text-red">{getTranslatedError(isError, t) || t('components.groupForm.errors.unknown')}</div>}
        </form>
      </FormProvider>
    </Modal>
  );
};
